/**
 * Crypt Base Component
 *
 * SVG component for player home base area using the crypt skin.
 * Features an ornate gold/bronze frame with zigzag edges, dark inner pawn
 * quadrants, player-specific main face gradients, and white bone/cross decorations.
 *
 * This component is color-agnostic and accepts a colorScheme prop to render
 * bases for different players (Red, Blue, Yellow, Green).
 */

import React, { type ReactElement } from 'react';
import type { BaseProps } from '../../constants/types';
import type { PawnProps } from '../../constants/types';
import { renderPawnAtPosition } from '../basics/utils/utils';
import type { CryptBaseColorScheme } from './constants/baseColorSchemes';

// Helper constants for rank display
const RANK_ICONS = ['🏆', '🥈', '🥉', '🏅'] as const;
const RANK_ORDINALS = ['1st', '2nd', '3rd', '4th'] as const;

function getRankIcon(rank: number): string {
  return RANK_ICONS[Math.min(rank - 1, 3)];
}

function getRankOrdinal(rank: number): string {
  if (rank >= 1 && rank <= 4) {
    return RANK_ORDINALS[rank - 1];
  }
  return `${rank.toString()}th`;
}

export interface CryptBaseComponentProps extends BaseProps {
  colorScheme: CryptBaseColorScheme;
  PawnComponent: React.ComponentType<PawnProps>;
  onPawn1Click?: () => void;
  onPawn2Click?: () => void;
  onPawn3Click?: () => void;
  onPawn4Click?: () => void;
  shouldAnimatePawns?: boolean;
  /** Player's finishing rank (1-4) - when set, shows rank badge instead of pawns */
  playerRank?: number;
  /** Whether the player has voluntarily left the game */
  hasLeft?: boolean;
}

const CryptBaseComponent = React.memo(function CryptBaseComponent({
  viewBox = '0 0 486 523',
  className = '',
  isActive = false,
  isHovered = false,
  isSelected = false,
  showEntrance = false,
  pawn1 = false,
  pawn2 = false,
  pawn3 = false,
  pawn4 = false,
  pawn1Active = false,
  pawn1Hovered = false,
  pawn1Selected = false,
  pawn1Moveable = false,
  pawn2Active = false,
  pawn2Hovered = false,
  pawn2Selected = false,
  pawn2Moveable = false,
  pawn3Active = false,
  pawn3Hovered = false,
  pawn3Selected = false,
  pawn3Moveable = false,
  pawn4Active = false,
  pawn4Hovered = false,
  pawn4Selected = false,
  pawn4Moveable = false,
  colorScheme,
  PawnComponent,
  onPawn1Click,
  onPawn2Click,
  onPawn3Click,
  onPawn4Click,
  shouldAnimatePawns = false,
  playerRank,
  hasLeft = false
}: CryptBaseComponentProps): ReactElement {
  const {
    name,
    ariaLabel,
    gradientPrimary,
    gradientSecondary,
    pawnGradientStart,
    pawnGradientEnd
  } = colorScheme;

  const svgClassName = [
    className,
    showEntrance ? 'base-entrance' : '',
    isActive ? 'base-active' : '',
    isHovered ? 'base-hovered' : '',
    isSelected ? 'base-selected' : ''
  ]
    .filter(Boolean)
    .join(' ');

  // Prefix all gradient/clip IDs to avoid conflicts when 4 bases render simultaneously
  const prefix = `${name}CryptBase`;

  return (
    <svg
      viewBox={viewBox}
      className={svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      aria-label={ariaLabel}
    >
      <defs>
        {/* paint0 — Bottom shadow dark: #2F3806 → #393D04 */}
        <linearGradient
          id={`${prefix}P0`}
          x1="246.1"
          y1="440.62"
          x2="246.1"
          y2="472.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2F3806" />
          <stop offset="1" stopColor="#393D04" />
        </linearGradient>

        {/* paint1 — Bottom shadow light: #95A14F → #6E7645 */}
        <linearGradient
          id={`${prefix}P1`}
          x1="246.12"
          y1="461.85"
          x2="246.12"
          y2="430.78"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#95A14F" />
          <stop offset="1" stopColor="#6E7645" />
        </linearGradient>

        {/* paint2 — Main face gradient horizontal (player-specific) */}
        <linearGradient
          id={`${prefix}P2`}
          x1="49.3201"
          y1="243.61"
          x2="442.93"
          y2="243.61"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={gradientPrimary} />
          <stop offset="0.24" stopColor={gradientSecondary} />
          <stop offset="0.47" stopColor={gradientPrimary} />
          <stop offset="0.75" stopColor={gradientSecondary} />
          <stop offset="1" stopColor={gradientPrimary} />
        </linearGradient>

        {/* paint3 — Main face gradient bottom-left diagonal (player-specific) */}
        <linearGradient
          id={`${prefix}P3`}
          x1="19.8801"
          y1="321.14"
          x2="290.95"
          y2="198.88"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={gradientPrimary} />
          <stop offset="0.24" stopColor={gradientSecondary} />
          <stop offset="0.47" stopColor={gradientPrimary} />
          <stop offset="0.75" stopColor={gradientSecondary} />
          <stop offset="1" stopColor={gradientPrimary} />
        </linearGradient>

        {/* paint4 — Main face gradient bottom-right diagonal (player-specific) */}
        <linearGradient
          id={`${prefix}P4`}
          x1="276.89"
          y1="368.54"
          x2="724.08"
          y2="368.54"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={gradientPrimary} />
          <stop offset="0.24" stopColor={gradientSecondary} />
          <stop offset="0.47" stopColor={gradientPrimary} />
          <stop offset="0.75" stopColor={gradientSecondary} />
          <stop offset="1" stopColor={gradientPrimary} />
        </linearGradient>

        {/* paint5 — Main face gradient top-left corner (player-specific) */}
        <linearGradient
          id={`${prefix}P5`}
          x1="6.90729e-05"
          y1="116.29"
          x2="236.16"
          y2="116.29"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={gradientPrimary} />
          <stop offset="0.24" stopColor={gradientSecondary} />
          <stop offset="0.47" stopColor={gradientPrimary} />
          <stop offset="0.75" stopColor={gradientSecondary} />
          <stop offset="1" stopColor={gradientPrimary} />
        </linearGradient>

        {/* paint6 — Pawn circle top-left (player-specific) */}
        <linearGradient
          id={`${prefix}P6`}
          x1="139.36"
          y1="179.24"
          x2="209.18"
          y2="179.24"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={pawnGradientStart} />
          <stop offset="1" stopColor={pawnGradientEnd} />
        </linearGradient>

        {/* paint7 — Pawn circle top-right (player-specific) */}
        <linearGradient
          id={`${prefix}P7`}
          x1="278.89"
          y1="177.89"
          x2="352.91"
          y2="177.89"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={pawnGradientStart} />
          <stop offset="1" stopColor={pawnGradientEnd} />
        </linearGradient>

        {/* paint8 — Pawn circle bottom-left (player-specific) */}
        <linearGradient
          id={`${prefix}P8`}
          x1="139.35"
          y1="311.19"
          x2="213.36"
          y2="311.19"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={pawnGradientStart} />
          <stop offset="1" stopColor={pawnGradientEnd} />
        </linearGradient>

        {/* paint9 — Pawn circle bottom-right (player-specific) */}
        <linearGradient
          id={`${prefix}P9`}
          x1="278.89"
          y1="311.86"
          x2="352.91"
          y2="311.86"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={pawnGradientStart} />
          <stop offset="1" stopColor={pawnGradientEnd} />
        </linearGradient>

        {/* paint10 — Right edge gold frame light: #876C3D → #947636 */}
        <linearGradient
          id={`${prefix}P10`}
          x1="405.37"
          y1="243.23"
          x2="443.56"
          y2="243.23"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>

        {/* paint11 — Right edge gold frame dark: #4C4018 → #443427 */}
        <linearGradient
          id={`${prefix}P11`}
          x1="447.44"
          y1="244.86"
          x2="416.36"
          y2="244.86"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4C4018" />
          <stop offset="1" stopColor="#443427" />
        </linearGradient>

        {/* paint12 — Left edge gold frame light: #876C3D → #947636 */}
        <linearGradient
          id={`${prefix}P12`}
          x1="49.3199"
          y1="243.85"
          x2="87.5099"
          y2="243.85"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>

        {/* paint13 — Left edge gold frame dark: #4C4018 → #443427 */}
        <linearGradient
          id={`${prefix}P13`}
          x1="72.4299"
          y1="242.84"
          x2="43.9899"
          y2="242.84"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4C4018" />
          <stop offset="1" stopColor="#443427" />
        </linearGradient>

        {/* paint14 — Top edge gold frame light: #876C3D → #947636 */}
        <linearGradient
          id={`${prefix}P14`}
          x1="246.09"
          y1="65.51"
          x2="246.09"
          y2="89.71"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>

        {/* paint15 — Top edge gold frame dark: #4C4018 → #443427 */}
        <linearGradient
          id={`${prefix}P15`}
          x1="247.1"
          y1="57.88"
          x2="247.1"
          y2="81.39"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4C4018" />
          <stop offset="1" stopColor="#443427" />
        </linearGradient>

        {/* paint16 — Bottom edge gold frame light: #876C3D → #947636 */}
        <linearGradient
          id={`${prefix}P16`}
          x1="58.83"
          y1="412.56"
          x2="434.66"
          y2="412.56"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>

        {/* paint17 — Bottom edge gold frame dark: #4C4018 → #443427 */}
        <linearGradient
          id={`${prefix}P17`}
          x1="245.73"
          y1="410.78"
          x2="245.73"
          y2="433.63"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4C4018" />
          <stop offset="1" stopColor="#443427" />
        </linearGradient>

        {/* paint18 — Top-right corner rivet: #876C3D → #947636 */}
        <linearGradient
          id={`${prefix}P18`}
          x1="409.23"
          y1="74.54"
          x2="1.00013"
          y2="74.54"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>

        {/* paint19 — Top-left corner rivet: #876C3D → #947636 */}
        <linearGradient
          id={`${prefix}P19`}
          x1="49.3199"
          y1="72.6199"
          x2="87.5099"
          y2="72.6199"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>

        {/* paint20 — Bottom-left corner rivet: #876C3D → #947636 */}
        <linearGradient
          id={`${prefix}P20`}
          x1="49.35"
          y1="412.65"
          x2="83.68"
          y2="412.65"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>

        {/* paint21 — Bottom-right corner rivet: #876C3D → #947636 */}
        <linearGradient
          id={`${prefix}P21`}
          x1="8.1579e-05"
          y1="414.6"
          x2="1.00008"
          y2="414.6"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>

        {/* Embedded texture pattern */}
        <pattern
          id={`${prefix}Pattern`}
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref={`#${prefix}Image`} transform="scale(0.00213675 0.00221239)" />
        </pattern>

        {/* Base64 texture image */}
        <image
          id={`${prefix}Image`}
          width="468"
          height="452"
          preserveAspectRatio="none"
          xlinkHref="data:image/png;base64,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"
        />
        {/* Rounded clip path matching basics outer frame shape.
            Coordinates in viewBox space (after translate applies).
            Top at y=41.89 (where crypt frame visually starts after translate -13.56).
            Tall height (480) avoids clipping bottom extrusion shadows. */}
        <clipPath id={`${prefix}RoundClip`}>
          <rect x="49" y="42" width="394" height="480" rx="30" ry="30" />
        </clipPath>
      </defs>

      {/*
        Translate crypt content up by ~13.56px to align the frame with the basics skin.
        The crypt frame starts at y≈55.45 while basics starts at y≈41.89.
        clipPath rounds all corners to match basics/celeste/magma skins.
      */}
      <g transform="translate(0, -13.56)" clipPath={`url(#${prefix}RoundClip)`}>
        {/* Drop shadow — outer bottom dark */}
        <g opacity="0.12">
          <path
            d="M49.3201 440.5V474.15C49.3201 484 54.5901 493.19 63.2701 497.85C63.3401 497.89 63.4101 497.92 63.4801 497.96C73.5401 503.27 83.0301 502.05 94.5001 501.52C108.48 500.87 122.46 502.93 136.45 502.92C164.33 502.92 192.22 502.96 220.1 502.92C240.14 502.89 259.89 502.91 274.01 502.92C295.63 502.94 306.45 502.95 307.57 502.97C355.08 503.71 358.86 506.92 375.71 502.97C387.36 500.24 391.53 497.3 402.41 497.45C412.72 497.59 419.32 500.37 427.84 496.94C434.8 494.13 439.79 487.53 439.79 487.53C439.8 487.51 439.82 487.5 439.83 487.48C441.85 485.13 442.91 482.11 442.88 479.01C442.74 464.39 442.6 449.77 442.46 435.15C379.63 444.58 306.67 451.34 225.48 451.3C160.92 451.26 101.85 446.93 49.3101 440.49L49.3201 440.5Z"
            fill="black"
          />
          <path
            d="M49.32 416.77V461.18C52.02 465.18 56.4901 470.54 63.4701 474.23C73.5301 479.54 83.02 478.32 94.49 477.79C108.47 477.14 122.45 479.2 136.44 479.19C164.32 479.19 192.21 479.23 220.09 479.19C240.13 479.16 259.88 479.18 274 479.19C295.62 479.21 306.44 479.22 307.56 479.24C355.07 479.98 358.85 483.19 375.7 479.24C387.35 476.51 391.52 473.57 402.4 473.72C412.71 473.86 419.31 476.64 427.83 473.21C434.79 470.4 439.78 463.8 439.78 463.8C441.18 462.18 442.22 460.71 442.92 459.65C442.76 443.57 442.61 427.5 442.45 411.42C379.62 420.85 306.66 427.61 225.47 427.57C160.91 427.53 101.84 423.2 49.3 416.76L49.32 416.77Z"
            fill="black"
          />
          <path
            d="M406.04 84.8599H86.2101C65.8363 84.8599 49.3201 101.376 49.3201 121.75V424.29C49.3201 444.664 65.8363 461.18 86.2101 461.18H406.04C426.414 461.18 442.93 444.664 442.93 424.29V121.75C442.93 101.376 426.414 84.8599 406.04 84.8599Z"
            fill="black"
          />
          <path
            d="M371.71 117.69H120.54C100.166 117.69 83.65 134.206 83.65 154.58V391.48C83.65 411.854 100.166 428.37 120.54 428.37H371.71C392.084 428.37 408.6 411.854 408.6 391.48V154.58C408.6 134.206 392.084 117.69 371.71 117.69Z"
            fill="black"
          />
          <path
            d="M372.9 125.53H119.34C104.158 125.53 91.8501 137.837 91.8501 153.02V393.02C91.8501 408.202 104.158 420.51 119.34 420.51H372.9C388.082 420.51 400.39 408.202 400.39 393.02V153.02C400.39 137.837 388.082 125.53 372.9 125.53Z"
            fill="black"
          />
        </g>

        {/* Bottom extrusion shadow dark */}
        <path
          d="M442.88 449.61C442.91 452.71 441.86 455.74 439.83 458.08C439.83 458.1 439.8 458.12 439.79 458.13C439.79 458.13 434.8 464.73 427.84 467.54C419.33 470.97 412.72 468.19 402.41 468.05C391.53 467.9 387.37 470.83 375.71 473.57C358.86 477.52 355.08 474.31 307.57 473.57C306.44 473.55 295.63 473.54 274.01 473.52C259.89 473.51 240.14 473.49 220.1 473.52C192.22 473.56 164.33 473.51 136.45 473.52C122.45 473.52 108.47 471.47 94.4901 472.12C83.0201 472.65 73.5301 473.87 63.4701 468.56C63.4001 468.52 63.3301 468.48 63.2601 468.45C54.5901 463.79 49.3201 454.59 49.3201 444.75V411.09C101.86 417.53 160.93 421.86 225.48 421.9C244.47 421.91 263.01 421.55 281.07 420.86C340.25 418.63 394.33 412.98 442.46 405.75C442.54 414.03 442.62 422.32 442.7 430.6C442.7 430.99 442.71 431.38 442.71 431.77C442.74 435.15 442.77 438.53 442.81 441.91C442.83 444.48 442.86 447.04 442.88 449.61Z"
          fill={`url(#${prefix}P0)`}
        />

        {/* Bottom extrusion shadow light */}
        <path
          d="M49.32 390.98V435.39C52.02 439.39 56.4901 444.75 63.4701 448.44C73.5301 453.75 83.02 452.53 94.49 452C108.47 451.35 122.45 453.41 136.44 453.4C164.32 453.4 192.21 453.44 220.09 453.4C240.13 453.37 259.88 453.39 274 453.4C295.62 453.42 306.44 453.43 307.56 453.45C355.07 454.19 358.85 457.4 375.7 453.45C387.35 450.72 391.52 447.78 402.4 447.93C412.71 448.07 419.31 450.85 427.83 447.42C434.79 444.61 439.78 438.01 439.78 438.01C441.18 436.39 442.22 434.92 442.92 433.86C442.76 417.78 442.61 401.71 442.45 385.63C379.62 395.06 306.66 401.82 225.47 401.78C160.91 401.74 101.84 397.41 49.3 390.97L49.32 390.98Z"
          fill={`url(#${prefix}P1)`}
        />

        {/* Main face horizontal fill (player-specific) — rounded rect with ~37px radius */}
        <path
          d="M406.04 55.45H86.21C65.84 55.45 49.32 71.97 49.32 92.34V394.88C49.32 415.25 65.84 431.77 86.21 431.77H406.04C426.41 431.77 442.93 415.25 442.93 394.88V92.34C442.93 71.97 426.41 55.45 406.04 55.45Z"
          fill={`url(#${prefix}P2)`}
        />

        {/* Main face bottom-left diagonal patch (player-specific) */}
        <path
          d="M93.3001 343C85.7301 352.41 72.8601 366.08 49.3201 390.67V318.2C72.6101 289.84 78.4201 287.94 83.6301 276.68C98.1301 245.37 73.0501 217.29 83.6801 209.71C95.1901 201.49 138.63 224.38 144.3 256.22C147.71 275.34 129.57 297.9 93.3001 343Z"
          fill={`url(#${prefix}P3)`}
        />

        {/* Main face bottom-right diagonal patch (player-specific) */}
        <path
          d="M442.93 306.4V431.77H288.36C285.4 428.08 282.94 424.4 281.07 420.86C277.07 413.31 275.76 406.41 277.94 401.51C278.72 399.76 279.92 398.31 281.49 397.11C293.15 388.18 325.06 393.01 353.4 392.6C361.87 392.47 370.01 391.88 377.2 390.31H377.21C387.78 388.01 396.29 383.6 400.73 375.48C412.04 354.78 387.63 326.88 402.51 311.62C409.89 304.05 421.93 304.73 442.93 306.4Z"
          fill={`url(#${prefix}P4)`}
        />

        {/* Main face top-left corner patch (player-specific) */}
        <path
          d="M232.05 105.34C230 107.82 227.33 109.37 224.15 110.21C219.54 111.45 213.87 111.21 207.48 110.21C181.65 106.23 144.21 90.0399 118.72 110.02C102.79 122.5 109.4 135.1 88.7001 154.19C78.8001 163.32 64.1801 172.53 49.8301 176.98C49.6601 177.03 49.4901 177.08 49.3201 177.13V104.77C49.4901 104.73 49.6601 104.69 49.8301 104.66C89.9301 96.15 101.57 95.6199 104.5 88.18C108.72 77.4799 88.8401 67.95 88.5201 55.45H223.12C235.37 73.52 240.38 95.2599 232.05 105.34Z"
          fill={`url(#${prefix}P5)`}
        />

        {/* Inner quadrant dark top-left — shadow */}
        <path
          d="M385.64 248.61V345.13C385.64 365.5 369.12 382.02 348.75 382.02H246.1V248.61H385.64Z"
          fill="#00074C"
        />
        {/* Inner quadrant dark bottom-left — shadow */}
        <path
          d="M106.56 248.61H246.09V382.01H143.45C123.08 382.01 106.56 365.5 106.56 345.13V248.61Z"
          fill="#00074C"
        />
        {/* Inner quadrant fill bottom-left: #3F3C33 */}
        <path
          d="M106.58 243.61H246.11V377.01H143.46C123.1 377.01 106.57 360.48 106.57 340.12V243.61H106.58Z"
          fill="#3F3C33"
        />
        {/* Inner quadrant fill top-right: #3F3C33 */}
        <path
          d="M385.66 243.61L246.13 243.61L246.13 110.21L348.78 110.21C369.14 110.21 385.67 126.74 385.67 147.1V243.62L385.66 243.61Z"
          fill="#3F3C33"
        />
        {/* Inner quadrant fill top-left: #201C19 */}
        <path
          d="M246.13 110.21V243.61H106.6V147.09C106.6 126.73 123.13 110.2 143.49 110.2H246.13V110.21Z"
          fill="#201C19"
        />
        {/* Inner quadrant fill bottom-right: #201C19 */}
        <path
          d="M246.13 377.02V243.62H385.66V340.14C385.66 360.5 369.13 377.03 348.77 377.03H246.13V377.02Z"
          fill="#201C19"
        />

        {/* Pawn spots — only visible when game is active (no rank, not left) */}
        {playerRank === undefined && !hasLeft && (
          <>
            {/* Pawn spot 1 top-left — shadow */}
            <path
              d="M176.37 211.05C196.81 211.05 213.38 195.599 213.38 176.54C213.38 157.481 196.81 142.03 176.37 142.03C155.93 142.03 139.36 157.481 139.36 176.54C139.36 195.599 155.93 211.05 176.37 211.05Z"
              fill="#00004C"
            />
            {/* Pawn spot 1 top-left — fill (player-specific) */}
            <path
              d="M176.37 213.75C196.81 213.75 213.38 198.299 213.38 179.24C213.38 160.181 196.81 144.73 176.37 144.73C155.93 144.73 139.36 160.181 139.36 179.24C139.36 198.299 155.93 213.75 176.37 213.75Z"
              fill={`url(#${prefix}P6)`}
            />
            {/* Pawn spot 2 top-right — shadow */}
            <path
              d="M315.9 209.7C336.34 209.7 352.91 194.249 352.91 175.19C352.91 156.131 336.34 140.68 315.9 140.68C295.46 140.68 278.89 156.131 278.89 175.19C278.89 194.249 295.46 209.7 315.9 209.7Z"
              fill="#00004C"
            />
            {/* Pawn spot 2 top-right — fill (player-specific) */}
            <path
              d="M315.9 212.4C336.34 212.4 352.91 196.949 352.91 177.89C352.91 158.831 336.34 143.38 315.9 143.38C295.46 143.38 278.89 158.831 278.89 177.89C278.89 196.949 295.46 212.4 315.9 212.4Z"
              fill={`url(#${prefix}P7)`}
            />
            {/* Pawn spot 3 bottom-left — shadow */}
            <path
              d="M176.35 343C196.79 343 213.36 327.549 213.36 308.49C213.36 289.431 196.79 273.98 176.35 273.98C155.91 273.98 139.34 289.431 139.34 308.49C139.34 327.549 155.91 343 176.35 343Z"
              fill="#00004C"
            />
            {/* Pawn spot 3 bottom-left — fill (player-specific) */}
            <path
              d="M176.35 345.7C196.79 345.7 213.36 330.249 213.36 311.19C213.36 292.131 196.79 276.68 176.35 276.68C155.91 276.68 139.34 292.131 139.34 311.19C139.34 330.249 155.91 345.7 176.35 345.7Z"
              fill={`url(#${prefix}P8)`}
            />
            {/* Pawn spot 4 bottom-right — shadow */}
            <path
              d="M315.9 343.67C336.34 343.67 352.91 328.219 352.91 309.16C352.91 290.101 336.34 274.65 315.9 274.65C295.46 274.65 278.89 290.101 278.89 309.16C278.89 328.219 295.46 343.67 315.9 343.67Z"
              fill="#00004C"
            />
            {/* Pawn spot 4 bottom-right — fill (player-specific) */}
            <path
              d="M315.9 346.37C336.34 346.37 352.91 330.919 352.91 311.86C352.91 292.8 336.34 277.35 315.9 277.35C295.46 277.35 278.89 292.8 278.89 311.86C278.89 330.919 295.46 346.37 315.9 346.37Z"
              fill={`url(#${prefix}P9)`}
            />
          </>
        )}

        {/* Texture overlay */}
        <rect x="12.4301" y="17.96" width="467" height="451" fill={`url(#${prefix}Pattern)`} />

        {/* Decorative bone/cross elements at opacity 0.4 */}
        <path
          opacity="0.4"
          d="M405.13 100.48C400.83 93.9801 389.77 92.9501 384.84 98.6601C380.87 103.26 381.51 111.51 384.89 114.22C385.98 115.1 387.55 117.11 387.55 117.11C387.55 117.11 387.21 120.87 386.54 123.59C387.13 123.68 387.73 123.78 388.32 123.88L388.98 121.24L389.87 120.81L389.08 124C389.75 124.11 390.42 124.21 391.08 124.32L391.85 121.21L392.78 120.76L391.86 124.45C392.57 124.56 393.29 124.68 394 124.79L394.81 121.53L395.7 121.1L394.75 124.91C395.58 125.05 396.41 125.18 397.25 125.31C397.46 123.31 397.66 121.32 397.87 119.32C398.85 119.35 399.81 119.2 400.74 118.81C405.91 116.63 409.35 106.84 405.14 100.49L405.13 100.48ZM394.24 109.44C392.73 110.64 387.36 108.33 387.12 105.02C386.98 103.14 388.51 100.83 390.13 100.78C391.17 100.75 391.97 101.67 392.52 102.31C394.44 104.55 395.52 108.42 394.24 109.44ZM403.79 107.08C402.11 109.93 396.27 109.6 395.45 107.86C394.75 106.38 397.44 103.39 400.16 102.25C400.94 101.92 402.06 101.45 402.98 101.94C404.42 102.7 404.74 105.45 403.79 107.08Z"
          fill="white"
        />
        <path
          opacity="0.4"
          d="M96.32 366.17C88.53 366.4 81.82 375.25 84.07 382.45C85.88 388.25 93.23 392.06 97.31 390.62C98.63 390.15 101.17 389.89 101.17 389.89C101.17 389.89 104.19 392.16 106.14 394.17C106.53 393.72 106.93 393.26 107.32 392.8L105.42 390.85L105.52 389.87L107.81 392.23C108.25 391.72 108.69 391.21 109.14 390.7L106.9 388.4L107.01 387.37L109.66 390.1C110.13 389.55 110.61 389.01 111.08 388.46L108.74 386.05L108.84 385.06L111.58 387.88C112.13 387.24 112.68 386.61 113.24 385.97C111.65 384.74 110.06 383.52 108.48 382.28C109.02 381.46 109.41 380.57 109.56 379.58C110.43 374.04 103.94 365.95 96.32 366.18V366.17ZM98.19 380.15C98.41 382.07 93.62 385.41 90.68 383.87C89.01 382.99 87.85 380.48 88.67 379.07C89.19 378.17 90.39 377.98 91.23 377.85C94.14 377.39 98.01 378.52 98.19 380.15ZM101.23 370.79C102.76 373.72 99.41 378.51 97.5 378.28C95.87 378.09 94.76 374.23 95.22 371.32C95.35 370.48 95.54 369.28 96.45 368.76C97.86 367.94 100.37 369.11 101.24 370.79H101.23Z"
          fill="white"
        />

        {/* Right decorative edge — shadow layer */}
        <path
          opacity="0.55"
          d="M440.45 55.9399V430.53C435.01 427.27 429.57 425.25 424.14 421.98V396.34C423.02 392.46 420.76 386.4 416.04 380.16C411.17 373.72 405.75 369.83 402.26 367.7C405.38 365.95 409.36 363.23 413.2 359.13C420.16 351.7 422.96 343.67 424.14 339.22V275.32C422.92 271.52 420.73 266.13 416.66 260.5C412.18 254.31 407.24 250.31 403.79 247.94C406.92 245.8 411.03 242.47 414.82 237.53C420.79 229.75 422.98 221.91 423.88 217.41C424.29 197.14 424.15 180.17 423.88 167.18C423.78 162.51 423.59 156.53 420.64 149.62C416.09 138.97 407.75 132.59 402.26 129.2C406.33 125.99 417.95 115.98 422.27 98.5699C425.15 86.9799 423.53 77.1999 422.27 72.0199C428.33 66.6599 434.39 61.2999 440.45 55.9399Z"
          fill="black"
        />
        {/* Right decorative edge — gold frame light */}
        <path
          d="M443.56 55.9399V430.53C438.12 427.27 432.68 425.25 427.25 421.98V396.34C426.13 392.46 423.87 386.4 419.15 380.16C414.28 373.72 408.86 369.83 405.37 367.7C408.49 365.95 412.47 363.23 416.31 359.13C423.27 351.7 426.07 343.67 427.25 339.22V275.32C426.03 271.52 423.84 266.13 419.77 260.5C415.29 254.31 410.35 250.31 406.9 247.94C410.03 245.8 414.14 242.47 417.93 237.53C423.9 229.75 426.09 221.91 426.99 217.41C427.4 197.14 427.26 180.17 426.99 167.18C426.89 162.51 426.7 156.53 423.75 149.62C419.2 138.97 410.86 132.59 405.37 129.2C409.44 125.99 421.06 115.98 425.38 98.5699C428.26 86.9799 426.64 77.1999 425.38 72.0199C431.44 66.6599 437.5 61.2999 443.56 55.9399Z"
          fill={`url(#${prefix}P10)`}
        />
        {/* Right decorative edge — gold frame dark */}
        <path
          d="M443.56 62.23V427.5C440.49 425.67 437.43 423.82 434.36 421.98V396.34C433.24 392.46 430.98 386.4 426.27 380.16C421.39 373.72 415.98 369.83 412.48 367.7C415.6 365.95 419.58 363.23 423.42 359.13C430.38 351.7 433.18 343.67 434.36 339.22V275.32C433.14 271.52 430.95 266.13 426.88 260.5C422.4 254.31 417.46 250.31 414.01 247.94C417.14 245.8 421.25 242.47 425.04 237.53C431.01 229.75 433.21 221.91 434.1 217.41C434.51 197.14 434.38 180.17 434.1 167.18C434 162.51 433.82 156.53 430.86 149.62C426.31 138.97 417.97 132.59 412.48 129.2C416.55 125.99 428.17 115.98 432.49 98.57C435.37 86.98 433.75 77.2 432.49 72.02C436.18 68.76 439.87 65.49 443.56 62.23Z"
          fill={`url(#${prefix}P11)`}
        />

        {/* Left decorative edge — shadow layer */}
        <path
          opacity="0.55"
          d="M52.23 431.77V55.9399C57.67 59.1999 63.11 62.4599 68.54 65.7299V91.3699C69.66 95.2499 71.92 101.31 76.64 107.55C81.51 113.99 86.93 117.88 90.42 120.01C87.3 121.76 83.32 124.48 79.48 128.58C72.52 136.01 69.72 144.04 68.54 148.49V212.39C69.76 216.19 71.95 221.58 76.02 227.21C80.5 233.4 85.44 237.4 88.89 239.77C85.76 241.91 81.65 245.24 77.86 250.18C71.89 257.96 69.7 265.8 68.8 270.3C68.39 290.57 68.53 307.54 68.8 320.53C68.9 325.2 69.09 331.18 72.04 338.09C76.59 348.74 84.93 355.12 90.42 358.51C86.35 361.72 74.73 371.73 70.41 389.14C67.53 400.73 69.15 410.51 70.41 415.69C64.35 421.05 58.29 426.41 52.23 431.77Z"
          fill="black"
        />
        {/* Left decorative edge — gold frame light */}
        <path
          d="M49.3199 431.77V55.9399C54.7599 59.1999 60.1999 62.4599 65.6299 65.7299V91.3699C66.7499 95.2499 69.01 101.31 73.7299 107.55C78.5999 113.99 84.0199 117.88 87.5099 120.01C84.3899 121.76 80.4099 124.48 76.5699 128.58C69.6099 136.01 66.8099 144.04 65.6299 148.49V212.39C66.8499 216.19 69.0399 221.58 73.1099 227.21C77.5899 233.4 82.5299 237.4 85.9799 239.77C82.8499 241.91 78.7399 245.24 74.9499 250.18C68.9799 257.96 66.7899 265.8 65.8899 270.3C65.4799 290.57 65.6199 307.54 65.8899 320.53C65.9899 325.2 66.1799 331.18 69.1299 338.09C73.6799 348.74 82.02 355.12 87.5099 358.51C83.4399 361.72 71.8199 371.73 67.4999 389.14C64.6199 400.73 66.2399 410.51 67.4999 415.69C61.4399 421.05 55.3799 426.41 49.3199 431.77Z"
          fill={`url(#${prefix}P12)`}
        />
        {/* Left decorative edge — gold frame dark */}
        <path
          d="M49.3199 425.48V60.21C52.3899 62.04 55.4499 63.89 58.5199 65.73V91.37C59.6399 95.25 61.8999 101.31 66.6099 107.55C71.4899 113.99 76.8999 117.88 80.3999 120.01C77.2799 121.76 73.2999 124.48 69.4599 128.58C62.4999 136.01 59.6999 144.04 58.5199 148.49V212.39C59.7399 216.19 61.9299 221.58 65.9999 227.21C70.4799 233.4 75.4199 237.4 78.8699 239.77C75.7399 241.91 71.6299 245.24 67.8399 250.18C61.8699 257.96 59.6699 265.8 58.7799 270.3C58.3699 290.57 58.4999 307.54 58.7799 320.53C58.8799 325.2 59.0599 331.18 62.0199 338.09C66.5699 348.74 74.9099 355.12 80.3999 358.51C76.3299 361.72 64.7099 371.73 60.3899 389.14C57.5099 400.73 59.1299 410.51 60.3899 415.69C56.6999 418.95 53.0099 422.22 49.3199 425.48Z"
          fill={`url(#${prefix}P13)`}
        />

        {/* Top decorative edge — shadow layer */}
        <path
          opacity="0.55"
          d="M58.1801 58.1199H434.01C430.75 63.5599 427.49 68.9999 424.22 74.4299H398.58C394.7 75.5499 388.64 77.8099 382.4 82.5299C375.96 87.3999 372.07 92.8199 369.94 96.3099C368.19 93.1899 365.47 89.2099 361.37 85.3699C353.94 78.4099 345.91 75.6099 341.46 74.4299H277.56C273.76 75.6499 268.37 77.8399 262.74 81.9099C256.55 86.3899 252.55 91.3299 250.18 94.7799C248.04 91.6499 244.71 87.5399 239.77 83.7499C231.99 77.7799 224.15 75.5899 219.65 74.6899C199.38 74.2799 182.41 74.4199 169.42 74.6899C164.75 74.7899 158.77 74.9799 151.86 77.9299C141.21 82.4799 134.83 90.8199 131.44 96.3099C128.23 92.2399 118.22 80.6199 100.81 76.2999C89.2201 73.4199 79.4401 75.0399 74.2601 76.2999C68.9001 70.2399 63.5401 64.1799 58.1801 58.1199Z"
          fill="black"
        />
        {/* Top decorative edge — gold frame light */}
        <path
          d="M58.1801 55.45H434.01C430.75 60.89 427.49 66.33 424.22 71.76H398.58C394.7 72.88 388.64 75.14 382.4 79.86C375.96 84.7299 372.07 90.1499 369.94 93.6399C368.19 90.52 365.47 86.5399 361.37 82.6999C353.94 75.7399 345.91 72.94 341.46 71.76H277.56C273.76 72.98 268.37 75.1699 262.74 79.2399C256.55 83.7199 252.55 88.66 250.18 92.11C248.04 88.9799 244.71 84.87 239.77 81.08C231.99 75.11 224.15 72.9199 219.65 72.0199C199.38 71.6099 182.41 71.7499 169.42 72.0199C164.75 72.1199 158.77 72.31 151.86 75.26C141.21 79.81 134.83 88.1499 131.44 93.6399C128.23 89.57 118.22 77.9499 100.81 73.6299C89.2201 70.7499 79.4401 72.3699 74.2601 73.6299C68.9001 67.5699 63.5401 61.51 58.1801 55.45Z"
          fill={`url(#${prefix}P14)`}
        />
        {/* Top decorative edge — gold frame dark */}
        <path
          d="M64.4701 55.45H429.74C427.91 58.52 426.06 61.58 424.22 64.65H398.58C394.7 65.77 388.64 68.0299 382.4 72.7399C375.96 77.6199 372.07 83.0299 369.94 86.5299C368.19 83.41 365.47 79.4299 361.37 75.5899C353.94 68.6299 345.91 65.83 341.46 64.65H277.56C273.76 65.87 268.37 68.0599 262.74 72.1299C256.55 76.6099 252.55 81.5499 250.18 85C248.04 81.8699 244.71 77.76 239.77 73.97C231.99 68 224.15 65.8 219.65 64.91C199.38 64.5 182.41 64.63 169.42 64.91C164.75 65.01 158.77 65.19 151.86 68.15C141.21 72.7 134.83 81.0399 131.44 86.5299C128.23 82.4599 118.22 70.8399 100.81 66.5199C89.2201 63.6399 79.4401 65.2599 74.2601 66.5199C71.0001 62.8299 67.7301 59.14 64.4701 55.45Z"
          fill={`url(#${prefix}P15)`}
        />

        {/* Bottom decorative edge — shadow layer */}
        <path
          opacity="0.55"
          d="M434.66 428.37H58.83C62.09 422.93 65.35 417.49 68.62 412.06H94.26C98.14 410.94 104.2 408.68 110.44 403.96C116.88 399.09 120.77 393.67 122.9 390.18C124.65 393.3 127.37 397.28 131.47 401.12C138.9 408.08 146.93 410.88 151.38 412.06H215.28C219.08 410.84 224.47 408.65 230.1 404.58C236.29 400.1 240.29 395.16 242.66 391.71C244.8 394.84 248.13 398.95 253.07 402.74C260.85 408.71 268.69 410.9 273.19 411.8C293.46 412.21 310.43 412.07 323.42 411.8C328.09 411.7 334.07 411.51 340.98 408.56C351.63 404.01 358.01 395.67 361.4 390.18C364.61 394.25 374.62 405.87 392.03 410.19C403.62 413.07 413.4 411.45 418.58 410.19C423.94 416.25 429.3 422.31 434.66 428.37Z"
          fill="black"
        />
        {/* Bottom decorative edge — gold frame light */}
        <path
          d="M434.66 431.65H58.83C62.09 426.21 65.35 420.77 68.62 415.34H94.26C98.14 414.22 104.2 411.96 110.44 407.24C116.88 402.37 120.77 396.95 122.9 393.46C124.65 396.58 127.37 400.56 131.47 404.4C138.9 411.36 146.93 414.16 151.38 415.34H215.28C219.08 414.12 224.47 411.93 230.1 407.86C236.29 403.38 240.29 398.44 242.66 394.99C244.8 398.12 248.13 402.23 253.07 406.02C260.85 411.99 268.69 414.18 273.19 415.08C293.46 415.49 310.43 415.35 323.42 415.08C328.09 414.98 334.07 414.79 340.98 411.84C351.63 407.29 358.01 398.95 361.4 393.46C364.61 397.53 374.62 409.15 392.03 413.47C403.62 416.35 413.4 414.73 418.58 413.47C423.94 419.53 429.3 425.59 434.66 431.65Z"
          fill={`url(#${prefix}P16)`}
        />
        {/* Bottom decorative edge — gold frame dark */}
        <path
          d="M428.37 431.65H63.1C64.93 428.58 66.78 425.52 68.62 422.45H94.26C98.14 421.33 104.2 419.07 110.44 414.36C116.88 409.48 120.77 404.07 122.9 400.57C124.65 403.69 127.37 407.67 131.47 411.51C138.9 418.47 146.93 421.27 151.38 422.45H215.28C219.08 421.23 224.47 419.04 230.1 414.97C236.29 410.49 240.29 405.55 242.66 402.1C244.8 405.23 248.13 409.34 253.07 413.13C260.85 419.1 268.69 421.3 273.19 422.19C293.46 422.6 310.43 422.47 323.42 422.19C328.09 422.09 334.07 421.91 340.98 418.95C351.63 414.4 358.01 406.06 361.4 400.57C364.61 404.64 374.62 416.26 392.03 420.58C403.62 423.46 413.4 421.84 418.58 420.58C421.84 424.27 425.11 427.96 428.37 431.65Z"
          fill={`url(#${prefix}P17)`}
        />

        {/* Top-right corner rivet — flat gold */}
        <path
          d="M443.56 55.45V76.3899C443.38 76.49 443.19 76.5899 442.98 76.7099C434.66 81.0999 409.23 93.6399 409.23 93.6399L418.73 55.45H443.56Z"
          fill="#72531C"
        />
        {/* Top-right corner rivet — gradient */}
        <path d="M409.23 93.6399L443.56 55.45H418.73L409.23 93.6399Z" fill={`url(#${prefix}P18)`} />

        {/* Top-left corner rivet — flat gold */}
        <path
          d="M49.3199 55.45H70.2599C70.3599 55.63 70.4599 55.8199 70.5799 56.0299C74.9699 64.3499 87.5099 89.7799 87.5099 89.7799L49.3199 80.2799V55.45Z"
          fill="#72531C"
        />
        {/* Top-left corner rivet — gradient */}
        <path
          d="M87.5099 89.7799L49.3199 55.45V80.2799L87.5099 89.7799Z"
          fill={`url(#${prefix}P19)`}
        />

        {/* Bottom-left corner rivet — flat gold */}
        <path
          d="M49.35 431.74V410.8C49.53 410.7 49.72 410.6 49.93 410.48C58.25 406.09 83.68 393.55 83.68 393.55L74.18 431.74H49.35Z"
          fill="#72531C"
        />
        {/* Bottom-left corner rivet — gradient */}
        <path d="M83.68 393.55L49.35 431.74H74.18L83.68 393.55Z" fill={`url(#${prefix}P20)`} />

        {/* Bottom-right corner rivet — flat gold */}
        <path
          d="M443.56 431.77H422.62C422.52 431.59 422.42 431.4 422.3 431.19C417.91 422.87 405.37 397.44 405.37 397.44L443.56 406.94V431.77Z"
          fill="#72531C"
        />
        {/* Bottom-right corner rivet — gradient */}
        <path d="M405.37 397.44L443.56 431.77V406.94L405.37 397.44Z" fill={`url(#${prefix}P21)`} />

        {/* Decorative bone/cross elements continued */}
        <path
          opacity="0.4"
          d="M149.69 97.9901L175.48 85.1001C175.48 85.1001 179.03 77.0401 181.6 81.0201C184.18 85.0001 181.6 84.8901 181.6 84.8901C181.6 84.8901 185.47 85.3201 184.72 87.7901C183.97 90.2601 178.6 88.9601 178.6 88.9601L151.42 101.33C151.42 101.33 148.2 107.24 147.12 104.55C146.05 101.86 148.52 101.11 148.52 101.11C148.52 101.11 143.47 101.43 144.44 98.7501C145.41 96.0601 149.7 98.0001 149.7 98.0001L149.69 97.9901Z"
          fill="white"
        />
        <path
          opacity="0.4"
          d="M171.33 98.82L199.94 102.37C199.94 102.37 207.36 97.62 207.3 102.37C207.25 107.11 205.16 105.59 205.16 105.59C205.16 105.59 208.14 108.09 206.15 109.73C204.16 111.37 200.4 107.31 200.4 107.31L170.92 102.55C170.92 102.55 164.96 105.68 165.56 102.85C166.16 100.02 168.63 100.76 168.63 100.76C168.63 100.76 164.25 98.23 166.54 96.53C168.83 94.83 171.34 98.82 171.34 98.82H171.33Z"
          fill="white"
        />
        <path
          opacity="0.4"
          d="M286.88 385.88L315.49 389.43C315.49 389.43 322.91 384.68 322.85 389.43C322.79 394.18 320.71 392.65 320.71 392.65C320.71 392.65 323.69 395.15 321.7 396.79C319.71 398.43 315.95 394.37 315.95 394.37L286.47 389.61C286.47 389.61 280.51 392.74 281.11 389.91C281.71 387.08 284.18 387.82 284.18 387.82C284.18 387.82 279.8 385.29 282.09 383.59C284.38 381.89 286.89 385.88 286.89 385.88H286.88Z"
          fill="white"
        />
        <path
          opacity="0.4"
          d="M344.13 395.26L317.28 405.76C317.28 405.76 313.02 413.46 310.81 409.27C308.6 405.08 311.16 405.42 311.16 405.42C311.16 405.42 307.35 404.64 308.32 402.25C309.29 399.86 314.52 401.64 314.52 401.64L342.71 391.78C342.71 391.78 346.46 386.19 347.28 388.96C348.11 391.73 345.58 392.26 345.58 392.26C345.58 392.26 350.64 392.4 349.43 394.98C348.22 397.57 344.12 395.25 344.12 395.25L344.13 395.26Z"
          fill="white"
        />
        <path
          opacity="0.4"
          d="M91.74 290.4L91.58 305.51C91.58 305.51 94.49 309.09 92.02 309.34C89.55 309.59 90.22 308.41 90.22 308.41C90.22 308.41 89.09 310.11 88.12 309.17C87.15 308.23 89.04 306.04 89.04 306.04L89.78 290.41C89.78 290.41 87.8 287.49 89.31 287.63C90.82 287.77 90.58 289.1 90.58 289.1C90.58 289.1 91.64 286.67 92.66 287.76C93.68 288.85 91.75 290.39 91.75 290.39L91.74 290.4Z"
          fill="white"
        />
        <path
          opacity="0.4"
          d="M86.1 316.63L80.34 305.72C80.34 305.72 76.86 304.28 78.54 303.14C80.22 302 80.2 303.1 80.2 303.1C80.2 303.1 80.35 301.44 81.41 301.74C82.47 302.04 81.97 304.35 81.97 304.35L87.52 315.86C87.52 315.86 90.08 317.18 88.94 317.67C87.8 318.16 87.46 317.1 87.46 317.1C87.46 317.1 87.65 319.26 86.49 318.87C85.33 318.48 86.12 316.62 86.12 316.62L86.1 316.63Z"
          fill="white"
        />
        <path
          opacity="0.4"
          d="M75.7101 300.44L82.9101 290.42C82.9101 290.42 82.65 286.66 84.42 287.65C86.1901 288.64 85.1901 289.12 85.1901 289.12C85.1901 289.12 86.74 288.51 86.95 289.6C87.16 290.69 84.8601 291.26 84.8601 291.26L77.02 301.36C77.02 301.36 76.9801 304.24 76.0301 303.44C75.0801 302.64 75.8701 301.86 75.8701 301.86C75.8701 301.86 74.02 302.99 73.85 301.78C73.68 300.57 75.6901 300.45 75.6901 300.45L75.7101 300.44Z"
          fill="white"
        />
        <path
          opacity="0.4"
          d="M406.67 150.38L410.53 164.99C410.53 164.99 414.29 167.67 411.97 168.57C409.65 169.47 409.99 168.16 409.99 168.16C409.99 168.16 409.36 170.1 408.17 169.45C406.98 168.8 408.22 166.18 408.22 166.18L404.78 150.91C404.78 150.91 402.09 148.63 403.59 148.36C405.09 148.09 405.2 149.44 405.2 149.44C405.2 149.44 405.57 146.81 406.85 147.6C408.13 148.39 406.67 150.38 406.67 150.38Z"
          fill="white"
        />
        <path
          opacity="0.4"
          d="M404.17 164.97L405.56 177.23C405.56 177.23 408.28 179.84 406.3 180.29C404.32 180.74 404.75 179.72 404.75 179.72C404.75 179.72 404.01 181.21 403.13 180.55C402.25 179.89 403.56 177.92 403.56 177.92L402.59 165.17C402.59 165.17 400.69 163 401.93 162.97C403.17 162.94 403.11 164.04 403.11 164.04C403.11 164.04 403.72 161.96 404.66 162.75C405.6 163.53 404.19 164.98 404.19 164.98L404.17 164.97Z"
          fill="white"
        />
        <path
          opacity="0.4"
          d="M393.88 164.32L398.16 152.75C398.16 152.75 396.91 149.2 398.88 149.68C400.85 150.17 400.01 150.89 400.01 150.89C400.01 150.89 401.34 149.89 401.83 150.89C402.32 151.88 400.26 153.05 400.26 153.05L395.39 164.87C395.39 164.87 396.11 167.66 394.99 167.13C393.87 166.6 394.42 165.65 394.42 165.65C394.42 165.65 392.94 167.23 392.45 166.11C391.96 164.99 393.87 164.33 393.87 164.33L393.88 164.32Z"
          fill="white"
        />
      </g>

      {/* Left badge display when player has voluntarily left the game */}
      {hasLeft && (
        <g>
          {/* Circle background - grey tint */}
          <circle cx="245" cy="246" r="100" fill="#666" />
          <circle cx="245" cy="246" r="85" fill="#444" />

          {/* Exit/Leave door icon */}
          <g transform="translate(185, 186)">
            {/* Door frame */}
            <rect
              x="15"
              y="10"
              width="50"
              height="100"
              rx="4"
              fill="#888"
              stroke="#aaa"
              strokeWidth="3"
            />
            {/* Door */}
            <rect x="20" y="15" width="40" height="90" rx="2" fill="#666" />
            {/* Door handle */}
            <circle cx="52" cy="60" r="4" fill="#ccc" />
            {/* Arrow pointing out */}
            <path
              d="M75 60 L100 60 M90 50 L100 60 L90 70"
              stroke="#fff"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </g>
        </g>
      )}

      {/* Rank badge display when player has finished (but not left) */}
      {playerRank !== undefined && !hasLeft && (
        <g>
          {/* Trophy circle background */}
          <circle cx="245" cy="246" r="100" fill="#3F3C33" />
          <circle cx="245" cy="246" r="90" fill={gradientSecondary} />

          {/* Trophy icon and rank text */}
          <text
            x="245"
            y="227"
            textAnchor="middle"
            fontSize="72"
            aria-label={`Rank ${playerRank.toString()} trophy`}
          >
            {getRankIcon(playerRank)}
          </text>
          <text x="245" y="297" textAnchor="middle" fontSize="48" fontWeight="bold" fill="#fff">
            {getRankOrdinal(playerRank)}
          </text>
        </g>
      )}

      {/* Render pawns conditionally (hidden when player has finished or left) */}
      {playerRank === undefined && !hasLeft && (
        <>
          {renderPawnAtPosition(
            'pawn1',
            pawn1,
            PawnComponent,
            {
              isActive: pawn1Active,
              isHovered: pawn1Hovered,
              isSelected: pawn1Selected,
              isMoveable: pawn1Moveable
            },
            onPawn1Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn2',
            pawn2,
            PawnComponent,
            {
              isActive: pawn2Active,
              isHovered: pawn2Hovered,
              isSelected: pawn2Selected,
              isMoveable: pawn2Moveable
            },
            onPawn2Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn3',
            pawn3,
            PawnComponent,
            {
              isActive: pawn3Active,
              isHovered: pawn3Hovered,
              isSelected: pawn3Selected,
              isMoveable: pawn3Moveable
            },
            onPawn3Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn4',
            pawn4,
            PawnComponent,
            {
              isActive: pawn4Active,
              isHovered: pawn4Hovered,
              isSelected: pawn4Selected,
              isMoveable: pawn4Moveable
            },
            onPawn4Click,
            shouldAnimatePawns
          )}
        </>
      )}
    </svg>
  );
});

export default CryptBaseComponent;
